import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { isSSEMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { RovoChatStreamTimeoutError } from '@atlassian/conversation-assistant-utils/RovoChatStreamTimeoutError';
import { withAsyncGeneratorTimeout } from '@atlassian/conversation-assistant-utils/withAsyncGeneratorTimeout';

import { buildPostFinalInterruptedEvent } from './buildPostFinalInterruptedEvent';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createReconnectableStream } from './createReconnectableStream';
import { createReconnectionMetrics } from './createReconnectionMetrics';
import { createStreamTimeoutPauseController } from './createStreamTimeoutPauseController';
import type { ChatReconnectableStreamOptions, SSEStreamChunk } from './reconnectable-stream';

const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

type ReconnectionTrigger = 'streamError' | 'streamTimeout';
type ReconnectionOutcome =
	| 'aborted'
	| 'conversationRefresh'
	| 'exhausted'
	| 'failed'
	| 'streamReconnect';

/**
 * Creates a reconnectable stream pre-wired with Rovo chat conventions:
 * - Reconnects via `assistanceService.reconnectToMessageStream`
 * - Extracts SSE event IDs from `ParsedSSEMessage` chunks
 * - Fires `rovoChatReconnection` analytics on each retry
 * - Tracks RECONNECT_TO_ACTIVE_STREAM experience (start/succeed/fail/abort)
 * - Tracks reconnection performance metrics (timeToConnect, timeToResume)
 * - Updates conversation traces with retry status
 * - Pauses reconnection when chat is unmounted
 *
 * Callers pass hooks for site-specific concerns (dead air tracking,
 * inactivity watchdogs, ID alignment, MAU events).
 */
export function createChatReconnectableStream({
	generator,
	conversationId,
	abortController,
	assistanceService,
	sendAnalyticsEvent,
	experienceTracker,
	getMessageId,
	getRequestId,
	canReconnect,
	hasProcessedFinalResponse,
	getInvalidatedFinalResponse,
	getState,
	traceId,
	updateCurrentConversation,
	getIsChatMounted,
	analyticsAttributes,
	onBeforeFirstReconnect: callerOnBeforeFirstReconnect,
	onReconnecting: callerOnReconnecting,
	onReconnected: callerOnReconnected,
	onFirstChunkAfterReconnect: callerOnFirstChunkAfterReconnect,
	streamTimeout,
}: ChatReconnectableStreamOptions): AsyncGenerator<SSEStreamChunk> {
	let totalAttempts = 0;
	let timeoutCount = 0;
	let recoveryStartedAt: number | undefined;
	let recoveryTrigger: ReconnectionTrigger | undefined;
	let conversationRefreshOutcome: 'recovered' | 'failed' | undefined;
	let perfMetrics: ReturnType<typeof createReconnectionMetrics> | undefined;
	const getRecoveryAttributes = (recoveryOutcome?: ReconnectionOutcome) => {
		const requestId = getRequestId?.();
		return {
			...analyticsAttributes,
			conversationId,
			messageId: getMessageId(),
			...(requestId !== undefined ? { requestId } : {}),
			traceId,
			recoveryTrigger,
			...(recoveryOutcome ? { recoveryOutcome } : {}),
			recoveryElapsedMs:
				recoveryStartedAt === undefined ? undefined : Math.max(0, Date.now() - recoveryStartedAt),
			timeoutCount,
			streamTimeoutRecoveryEnabled: Boolean(streamTimeout),
		};
	};
	const cancelReconnectMetrics = () => {
		perfMetrics?.timeToConnectToStreamMetrics.cancel();
		perfMetrics?.timeToResumeStreamMetrics.cancel();
	};
	const timeoutPauseController = streamTimeout
		? createStreamTimeoutPauseController(streamTimeout.timeoutMs)
		: undefined;
	const wrapWithTimeout = (stream: AsyncGenerator<SSEStreamChunk>) =>
		streamTimeout
			? withAsyncGeneratorTimeout(stream, {
					timeoutMs: streamTimeout.timeoutMs,
					onTimeout: () => {
						const error = streamTimeout.onTimeout();
						if (error) {
							timeoutCount++;
						}
						return error;
					},
					isPaused: timeoutPauseController?.isPaused,
				})
			: stream;

	const reconnectableStream = createReconnectableStream<SSEStreamChunk>({
		initialGenerator: wrapWithTimeout(generator),

		canReconnect: () => !!getMessageId() && canReconnect(),
		completion: hasProcessedFinalResponse
			? {
					isProcessed: hasProcessedFinalResponse,
					onReadError: ({ error, reconnectAttempts }) => {
						const invalidatingEventType = getInvalidatedFinalResponse?.();
						if (invalidatingEventType) {
							sendAnalyticsEvent(
								buildPostFinalInterruptedEvent({
									conversationId,
									messageId: getMessageId(),
									traceId,
									error,
									reconnectAttempts,
									recoveryOutcome: 'completionInvalidated',
									invalidatingEventType,
									attributes: analyticsAttributes,
								}),
							);
						}
					},
					onRecoveryExhausted: ({ error, failureMode, reconnectAttempts }) => {
						clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
						cancelReconnectMetrics();
						if (reconnectAttempts > 0) {
							experienceTracker.abort({
								name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
								reason: 'completedAnswerPreserved',
							});
						}
						sendAnalyticsEvent(
							buildPostFinalInterruptedEvent({
								conversationId,
								messageId: getMessageId(),
								traceId,
								error,
								failureMode,
								reconnectAttempts,
								attributes: analyticsAttributes,
							}),
						);
					},
				}
			: undefined,

		getReconnectGenerator: (lastEventId) => {
			const messageId = getMessageId();
			if (!messageId) {
				return Promise.reject(new Error('Cannot reconnect: messageId is not available'));
			}
			return assistanceService
				.reconnectToMessageStream({
					conversationId,
					messageId,
					lastEventId,
					controller: abortController,
				})
				.then((r: { generator: AsyncGenerator<SSEStreamChunk> }) => wrapWithTimeout(r.generator));
		},

		getEventId: (chunk) => (isSSEMessage(chunk) && chunk.id ? chunk.id : undefined),

		onBeforeFirstReconnect: ({ streamError }) => {
			recoveryStartedAt = Date.now();
			recoveryTrigger =
				streamError instanceof RovoChatStreamTimeoutError ? 'streamTimeout' : 'streamError';
			perfMetrics = createReconnectionMetrics({
				isDeepResearchEnabled: analyticsAttributes?.isDeepResearch as boolean | undefined,
				thinkDeeperEnabled: analyticsAttributes?.thinkDeeperEnabled as boolean | undefined,
				fastModeEnabled: analyticsAttributes?.fastModeEnabled as boolean | undefined,
			});
			perfMetrics.timeToConnectToStreamMetrics.start();
			experienceTracker.start({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: getRecoveryAttributes(),
			});
			callerOnBeforeFirstReconnect?.();
		},

		onReconnecting: (attempt, maxAttempts, lastEventId) => {
			totalAttempts = attempt;
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'retrying',
				actionSubject: 'rovoChatReconnection',
				attributes: {
					attempt,
					maxAttempts,
					hasLastEventId: !!lastEventId,
					...getRecoveryAttributes(),
				},
			});
			updateCurrentConversation({
				traces: [
					{
						messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
						status: 'loading',
					},
				],
			});
			callerOnReconnecting?.(attempt, maxAttempts);
		},

		onReconnected: () => {
			perfMetrics?.timeToConnectToStreamMetrics.stop();
			perfMetrics?.timeToResumeStreamMetrics.start();
			callerOnReconnected?.();
		},

		onFirstChunkAfterReconnect: () => {
			perfMetrics?.timeToResumeStreamMetrics.stop();
			callerOnFirstChunkAfterReconnect?.();
		},

		onStreamCompleted: () => {
			experienceTracker.succeed({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: {
					totalAttempts,
					...getRecoveryAttributes(
						conversationRefreshOutcome === 'recovered' ? 'conversationRefresh' : 'streamReconnect',
					),
				},
			});
		},

		onStreamFailed: (error) => {
			cancelReconnectMetrics();

			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
					attributes: {
						totalAttempts,
						...getRecoveryAttributes('aborted'),
					},
				});
			} else {
				experienceTracker.fail({
					teamName: 'Rovo Chat',
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error instanceof Error ? error : new Error(String(error)),
					attributes: {
						totalAttempts,
						...getRecoveryAttributes(
							conversationRefreshOutcome === 'failed' ? 'exhausted' : 'failed',
						),
					},
				});
			}
		},

		onReconnectFailure: async ({ streamError, reconnectError }) => {
			if (abortController.signal.aborted || !(streamError instanceof RovoChatStreamTimeoutError)) {
				return false;
			}

			if (!streamTimeout?.onReconnectFailure) {
				return false;
			}

			conversationRefreshOutcome = 'failed';
			const recovered = await streamTimeout.onReconnectFailure(reconnectError ?? streamError);
			conversationRefreshOutcome = recovered ? 'recovered' : 'failed';
			return recovered;
		},

		shouldContinueRetrying: getIsChatMounted
			? () => {
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return true;
				}
			: undefined,

		signal: abortController.signal,
	});

	if (!timeoutPauseController) {
		return reconnectableStream;
	}

	return (async function* streamWithTimeoutLifecycle() {
		try {
			yield* reconnectableStream;
		} finally {
			timeoutPauseController.dispose();
		}
	})();
}
